import React, { useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { updateOneReservations } from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

export interface ActualCheckInOutProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
  config: {
    userRole: any;
    userRoles: any;
  };
  eventHandlers: {
    onGetOneReservation: (id: string) => void;
  };
}

const CAN_EDIT_ROLES = [
  UserRole.HOSPITALITY_AGENT,
  UserRole.HOSPITALITY_MANAGER,
  UserRole.GES,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN
];

export const ActualCheckInOut: React.FC<ActualCheckInOutProps> = ({
  data: { reservation },
  config: { userRole, userRoles },
  eventHandlers: { onGetOneReservation }
}) => {
  const [actualCheckInDate, setActualCheckInDate] = useState<Date | null>(null);
  const [actualCheckOutDate, setActualCheckOutDate] = useState<Date | null>(null);
  const [showCheckOutConfirmModal, setShowCheckOutConfirmModal] = useState<boolean>(false);
  const [isSavingActualCheckIn, setIsSavingActualCheckIn] = useState<boolean>(false);
  const [isSavingActualCheckOut, setIsSavingActualCheckOut] = useState<boolean>(false);

  useEffect(() => {
    if (!reservation) return;
    setActualCheckInDate(
      reservation.actualCheckInAt ? new Date(reservation.actualCheckInAt) : null
    );
    setActualCheckOutDate(
      reservation.actualCheckOutAt ? new Date(reservation.actualCheckOutAt) : null
    );
  }, [reservation]);

  const canEditActualCheckInOut =
    CAN_EDIT_ROLES.indexOf(userRole) > -1 ||
    CAN_EDIT_ROLES.some((item) => userRoles?.includes(item));

  const canEditActualDates =
    reservation &&
    canEditActualCheckInOut &&
    ![
      ReservationStatus.CANCELED,
      ReservationStatus.EXPIRED,
      ReservationStatus.CONFIRMATION_PENDING
    ].includes(reservation.status as ReservationStatus);

  const handleSaveActualCheckIn = () => {
    if (!reservation || !actualCheckInDate) return;
    setIsSavingActualCheckIn(true);
    const promise = updateOneReservations({
      id: reservation.id,
      actualCheckInAt: actualCheckInDate.getTime()
    }).then(() => {
      onGetOneReservation(reservation.id);
      setIsSavingActualCheckIn(false);
    });
    birdToast.promise(promise, {
      loadingMessage: 'Saving actual check-in time...',
      successMessage: 'Actual check-in time saved successfully.',
      errorFailedMessage: 'Failed to save actual check-in time.'
    });
  };

  const handleSaveActualCheckOut = () => {
    if (!reservation || !actualCheckOutDate) return;
    setShowCheckOutConfirmModal(true);
  };

  const confirmSaveActualCheckOut = () => {
    if (!reservation || !actualCheckOutDate) return;
    setIsSavingActualCheckOut(true);
    const promise = updateOneReservations({
      id: reservation.id,
      actualCheckOutAt: actualCheckOutDate.getTime()
    }).then(() => {
      onGetOneReservation(reservation.id);
      setIsSavingActualCheckOut(false);
      setShowCheckOutConfirmModal(false);
    });
    birdToast.promise(promise, {
      loadingMessage: 'Saving actual check-out time...',
      successMessage: 'Actual check-out time saved successfully.',
      errorFailedMessage: 'Failed to save actual check-out time.'
    });
  };

  return (
    <>
      <Modal
        config={{ showModal: showCheckOutConfirmModal, fullScreen: false, showClose: true }}
        eventHandlers={{
          closeModalHandler: () => setShowCheckOutConfirmModal(false)
        }}
      >
        <div className="p-8">
          <div className="font-semibold text-[1.8rem] text-[#566346] mb-4">
            Confirm Actual Check-out
          </div>
          <div className="font-[Abel] text-[1.4rem] text-[#333] mb-4 leading-normal">
            Are you sure you want to record the actual check-out time? This action may affect
            billing and reporting.
          </div>
          <div className="font-[Abel] text-[1.4rem] text-[#237c58] font-bold mb-6 p-3 bg-[#f5f5f5] rounded">
            Check-out time: {actualCheckOutDate?.toLocaleString()}
          </div>
          <div className="flex gap-4 justify-end">
            <Button
              onClick={() => setShowCheckOutConfirmModal(false)}
              type={ButtonType.primaryInverted}
              size={ButtonSize.medium}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSaveActualCheckOut}
              size={ButtonSize.medium}
              loading={isSavingActualCheckOut}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Actual Check-in */}
          <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
            <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
              Actual Check-in
            </span>
            <div className="flex flex-row gap-1 items-center h-[38px]">
              <ReactDatePicker
                selected={actualCheckInDate}
                onChange={(date) => setActualCheckInDate(date as Date | null)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMM dd, yyyy HH:mm"
                disabled={!canEditActualDates}
                placeholderText="Select date & time"
                className="w-full h-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 disabled:bg-gray-100 disabled:text-gray-400"
              />
              {canEditActualDates && (
                <Button
                  onClick={handleSaveActualCheckIn}
                  size={ButtonSize.small}
                  disabled={!actualCheckInDate || isSavingActualCheckIn}
                  loading={isSavingActualCheckIn}
                  className="!h-full"
                  classNameButton="!h-full !py-0 flex items-center justify-center"
                >
                  Save
                </Button>
              )}
            </div>
          </div>

          {/* Actual Check-out */}
          <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
            <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
              Actual Check-out
            </span>
            <div className="flex flex-row gap-1 items-center h-[38px]">
              <ReactDatePicker
                selected={actualCheckOutDate}
                onChange={(date) => setActualCheckOutDate(date as Date | null)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="MMM dd, yyyy HH:mm"
                disabled={!canEditActualDates}
                placeholderText="Select date & time"
                className="w-full h-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 disabled:bg-gray-100 disabled:text-gray-400"
              />
              {canEditActualDates && (
                <Button
                  onClick={handleSaveActualCheckOut}
                  size={ButtonSize.small}
                  disabled={!actualCheckOutDate || isSavingActualCheckOut}
                  loading={isSavingActualCheckOut}
                  className="!h-full"
                  classNameButton="!h-full !py-0 flex items-center justify-center"
                >
                  Save
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
